import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'

interface MentionUser {
  id: string
  name: string
  email: string
}

interface MentionInputProps {
  value: string
  onChange: (value: string) => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  onSend?: () => void
  placeholder?: string
  className?: string
  teamMembers: MentionUser[]
  autoFocus?: boolean
  disabled?: boolean
}

export interface MentionInputRef {
  focus: () => void
}

const MentionInput = forwardRef<MentionInputRef, MentionInputProps>(({
  value,
  onChange,
  onKeyDown,
  onSend,
  placeholder,
  className,
  teamMembers,
  autoFocus,
  disabled
}, ref) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionStartIdx, setMentionStartIdx] = useState(-1)
  const [selectedIdx, setSelectedIdx] = useState(0)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus()
  }))

  // Filter team members based on query
  const everyoneOption: MentionUser = { id: 'everyone', name: 'everyone', email: 'Notify all team members' }
  const filteredMembers = [
    ...(mentionQuery === '' || 'everyone'.startsWith(mentionQuery.toLowerCase()) ? [everyoneOption] : []),
    ...teamMembers.filter(m =>
      m.name.toLowerCase().includes(mentionQuery.toLowerCase()) ||
      m.email.toLowerCase().includes(mentionQuery.toLowerCase())
    )
  ].slice(0, 6)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    const cursorPos = e.target.selectionStart || 0
    onChange(newValue)

    // Check if we're in a mention context
    const textBeforeCursor = newValue.slice(0, cursorPos)
    const lastAtIdx = textBeforeCursor.lastIndexOf('@')

    if (lastAtIdx >= 0) {
      const textAfterAt = textBeforeCursor.slice(lastAtIdx + 1)
      // Only show dropdown if @ is at start or preceded by a space, and no space in the query
      const charBefore = lastAtIdx > 0 ? newValue[lastAtIdx - 1] : ' '
      if (charBefore === ' ' || lastAtIdx === 0) {
        if (!textAfterAt.includes(' ') || textAfterAt.split(' ').length <= 2) {
          setMentionStartIdx(lastAtIdx)
          setMentionQuery(textAfterAt)
          setShowDropdown(true)
          setSelectedIdx(0)
          return
        }
      }
    }

    setShowDropdown(false)
  }

  const insertMention = (member: MentionUser) => {
    const before = value.slice(0, mentionStartIdx)
    const after = value.slice(mentionStartIdx + mentionQuery.length + 1)
    const mention = member.id === 'everyone' ? '@everyone' : `@${member.name}`
    const newValue = `${before}${mention} ${after}`
    onChange(newValue)
    setShowDropdown(false)
    setMentionQuery('')
    setMentionStartIdx(-1)
    // Focus back on input
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showDropdown && filteredMembers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIdx(prev => (prev + 1) % filteredMembers.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIdx(prev => (prev - 1 + filteredMembers.length) % filteredMembers.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        insertMention(filteredMembers[selectedIdx])
        return
      }
      if (e.key === 'Escape') {
        setShowDropdown(false)
        return
      }
    }

    if (e.key === 'Enter' && !e.shiftKey && !showDropdown) {
      e.preventDefault()
      onSend?.()
      return
    }

    onKeyDown?.(e)
  }

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div className="relative flex-1">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={disabled}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
      />

      {/* Mention autocomplete dropdown */}
      {showDropdown && filteredMembers.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute bottom-full left-0 right-0 mb-1 bg-popover border border-border rounded-lg shadow-lg overflow-hidden z-50 backdrop-blur-xl"
        >
          <div className="p-1">
            {filteredMembers.map((member, idx) => (
              <button
                key={member.id}
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors',
                  idx === selectedIdx ? 'bg-primary/10 text-foreground' : 'hover:bg-muted/50 text-foreground'
                )}
                onMouseDown={(e) => {
                  e.preventDefault()
                  insertMention(member)
                }}
                onMouseEnter={() => setSelectedIdx(idx)}
              >
                {member.id === 'everyone' ? (
                  <div className="h-7 w-7 rounded-full bg-amber-500/15 flex items-center justify-center text-amber-500 text-xs font-bold">
                    @
                  </div>
                ) : (
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="text-[10px] bg-gradient-to-br from-primary/80 to-primary text-white font-semibold">
                      {member.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                )}
                <div className="flex-1 min-w-0 text-left">
                  <p className="font-medium truncate text-sm">
                    {member.id === 'everyone' ? '@everyone' : member.name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
})

MentionInput.displayName = 'MentionInput'
export default MentionInput

// Utility: extract mention IDs from a message
export function extractMentions(text: string, teamMembers: MentionUser[]): { userIds: string[], hasEveryone: boolean } {
  const userIds: string[] = []
  let hasEveryone = false

  if (text.includes('@everyone')) {
    hasEveryone = true
  }

  for (const member of teamMembers) {
    if (text.includes(`@${member.name}`)) {
      userIds.push(member.id)
    }
  }

  return { userIds, hasEveryone }
}

// Utility: render message text with highlighted mentions
export function renderWithMentions(
  text: string,
  teamMembers: MentionUser[],
  options?: { isMine?: boolean; onMentionClick?: (userId: string) => void }
): React.ReactNode[] {
  const mentionPattern = /@(everyone|[A-Za-z][A-Za-z\s]*[A-Za-z])(?=\s|$|[.,!?])/g
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match

  while ((match = mentionPattern.exec(text)) !== null) {
    const name = match[1]
    const member = teamMembers.find(m => m.name === name)
    const isValid = name === 'everyone' || member

    if (isValid) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index))
      }

      const isMine = options?.isMine
      parts.push(
        <span
          key={match.index}
          className={
            isMine
              ? 'bg-white/20 text-white font-semibold px-1 rounded cursor-pointer hover:bg-white/30 transition-colors'
              : 'bg-primary/15 text-primary font-semibold px-1 rounded cursor-pointer hover:bg-primary/25 transition-colors'
          }
          onClick={() => member && options?.onMentionClick?.(member.id)}
        >
          @{name}
        </span>
      )
      lastIndex = match.index + match[0].length
    }
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}
